// v3.21-1608 · Internal-only Ambiguous Intakes review queue.
//
// Lists pending ambiguous_intakes surfaced by the shared Household
// Matching Engine.  Operators inspect the incoming payload + top match
// candidates, then pick ONE of the six spec-mandated resolutions:
//
//   Update Client 1  · Update Client 2  · Create New  ·
//   Link Existing Deal  · Merge Household  · Reject
//
// External partners cannot see the sidebar link nor reach the API
// (server returns HTTP 403; UI renders a friendly banner in that case).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Loader2, AlertTriangle, CheckCircle2, ShieldAlert, X, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { isInternalUser } from '../lib/isInternalUser';

const RESOLUTIONS = [
  { id: 'update_client_1',    label: 'Update Client 1',    danger: true,  requiresClient: true },
  { id: 'update_client_2',    label: 'Update Client 2',    danger: false, requiresClient: true },
  { id: 'auto_client_2_fill', label: 'Fill Empty Client 2', hidden: true }, // engine-only
  { id: 'create_new',         label: 'Create New Deal',    danger: false, requiresClient: false },
  { id: 'link_existing_deal', label: 'Link Existing Deal', danger: false, requiresClient: true },
  { id: 'merge_household',    label: 'Merge Household',    danger: true,  requiresClient: true },
  { id: 'reject',             label: 'Reject',             danger: true,  requiresClient: false },
];

function maskSsn(v) {
  const s = String(v || '').replace(/\D/g, '');
  if (!s) return '';
  if (s.length < 4) return '***';
  return `***-**-${s.slice(-4)}`;
}

export default function AmbiguousIntakesPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/household-intake/ambiguous', { params: { status: 'pending' } });
      setItems(r.data?.items || []);
      setForbidden(false);
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(true);
      else setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const canView = useMemo(() => isInternalUser(user), [user]);

  if (!canView || forbidden) {
    return (
      <Card data-testid="ai-forbidden-page">
        <CardHeader><CardTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-rose-600" /> Access denied</CardTitle></CardHeader>
        <CardContent><p className="text-sm text-slate-600">Ambiguous Intakes is available to Prestigious Companies internal users only.</p></CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="ambiguous-intakes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Ambiguous Intakes</h1>
          <p className="text-xs text-slate-500">SSOT household matching queue · {items.length} pending</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={load} data-testid="ai-refresh"><RefreshCw className="h-3.5 w-3.5 mr-1" />Refresh</Button>
          <Button size="sm" variant="outline" onClick={() => nav('/deals')}>Back to Deals</Button>
        </div>
      </div>

      {loading ? (
        <div className="p-6 flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading queue…</div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 flex items-center gap-2" data-testid="ai-empty">
          <CheckCircle2 className="h-4 w-4" /> Queue is clear — no ambiguous intakes waiting.
        </div>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm" data-testid="ai-table">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">Received</th>
                  <th className="text-left px-3 py-2">Source</th>
                  <th className="text-left px-3 py-2">Incoming Name</th>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-left px-3 py-2">Phone</th>
                  <th className="text-left px-3 py-2">Address</th>
                  <th className="text-left px-3 py-2">Best Match</th>
                  <th className="text-right px-3 py-2">1st</th>
                  <th className="text-right px-3 py-2">2nd</th>
                  <th className="text-left px-3 py-2">Reason</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((row) => {
                  const inc = row.incoming || {};
                  const top = (row.candidates || [])[0] || {};
                  const second = (row.candidates || [])[1] || {};
                  return (
                    <tr key={row.id} className="border-t hover:bg-slate-50" data-testid={`ai-row-${row.id}`}>
                      <td className="px-3 py-2 text-xs whitespace-nowrap">{row.created_at ? new Date(row.created_at).toLocaleString() : ''}</td>
                      <td className="px-3 py-2 text-xs"><Badge variant="outline">{inc.source || row.source || 'unknown'}</Badge></td>
                      <td className="px-3 py-2 font-medium">{inc.full_name || '—'}</td>
                      <td className="px-3 py-2 text-xs">{inc.email || '—'}</td>
                      <td className="px-3 py-2 text-xs">{inc.phone || inc.mobile_phone || '—'}</td>
                      <td className="px-3 py-2 text-xs">{[inc.street_address, inc.city, inc.state, inc.zip_code].filter(Boolean).join(', ') || '—'}</td>
                      <td className="px-3 py-2 text-xs">{top.full_name || top.client_id || '—'}</td>
                      <td className="px-3 py-2 text-right text-xs font-mono">{top.confidence ?? '—'}</td>
                      <td className="px-3 py-2 text-right text-xs font-mono text-slate-400">{second.confidence ?? '—'}</td>
                      <td className="px-3 py-2 text-xs text-slate-600 max-w-[240px] truncate" title={row.reasoning}>{row.reasoning}</td>
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" onClick={() => setSelected(row)} data-testid={`ai-review-${row.id}`}>Review</Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {selected && <ResolutionDrawer row={selected} onClose={() => setSelected(null)} onResolved={() => { setSelected(null); load(); }} />}
    </div>
  );
}

function ResolutionDrawer({ row, onClose, onResolved }) {
  const inc = row.incoming || {};
  const candidates = row.candidates || [];
  const [resolution, setResolution] = useState('update_client_2');
  const [targetClientId, setTargetClientId] = useState((candidates[0] || {}).client_id || '');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const chosen = RESOLUTIONS.find((r) => r.id === resolution);

  const submit = async () => {
    if (chosen?.requiresClient && !targetClientId) {
      toast.error('Pick a target client / deal ID first.'); return;
    }
    // Confirmation for destructive actions
    if (chosen?.danger) {
      const label = chosen.label;
      const ok = window.confirm(`${label} — this is a destructive / identity-affecting action.\n\nProceed?`);
      if (!ok) return;
    }
    setBusy(true);
    try {
      await api.post(`/household-intake/ambiguous/${row.id}/resolve`, {
        resolution, target_client_id: targetClientId || null, note: note || null,
      });
      toast.success(`Applied: ${chosen.label}`);
      onResolved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resolve failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1100] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-[880px] max-w-[95vw] max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="ai-drawer">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" /> Ambiguous intake review</h2>
            <p className="text-[11px] text-slate-500 mt-0.5">{row.reasoning}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-5">
          <section data-testid="ai-incoming-panel">
            <h3 className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Incoming data</h3>
            <dl className="text-sm space-y-1">
              <Row label="Full name" value={inc.full_name} />
              <Row label="DOB"       value={inc.dob || inc.date_of_birth} />
              <Row label="Email"     value={inc.email} />
              <Row label="Phone"     value={inc.phone || inc.mobile_phone} />
              <Row label="Address"   value={[inc.street_address, inc.city, inc.state, inc.zip_code].filter(Boolean).join(', ')} />
              <Row label="SSN"       value={maskSsn(inc.ssn)} />
              <Row label="Credit App ID" value={inc.credit_app_confirmation_number || inc.credit_app_id} />
              <Row label="Deal ID (hint)" value={inc.deal_id} />
              <Row label="Source" value={inc.source || row.source} />
            </dl>
          </section>

          <section data-testid="ai-candidates-panel">
            <h3 className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Match candidates</h3>
            {candidates.length === 0 && <div className="text-xs text-slate-500">No candidates found.</div>}
            {candidates.slice(0, 5).map((c, i) => (
              <label key={`${c.client_id}-${c.applicant}`} className={`block border rounded-md p-2 mb-2 cursor-pointer ${targetClientId === c.client_id ? 'border-emerald-500 bg-emerald-50/40' : 'hover:bg-slate-50'}`}>
                <input type="radio" checked={targetClientId === c.client_id} onChange={() => setTargetClientId(c.client_id)} className="mr-2" data-testid={`ai-cand-radio-${i}`} />
                <span className="text-sm font-medium">{c.full_name || c.client_id}</span>
                <span className="ml-2 text-xs text-slate-500">slot {c.applicant} · {c.tier}</span>
                <span className={`ml-2 text-xs font-mono ${c.confidence >= 75 ? 'text-emerald-700' : c.confidence >= 50 ? 'text-amber-700' : 'text-slate-500'}`}>score {c.confidence}</span>
                {c.email && <div className="text-[11px] text-slate-500 mt-0.5">{c.email}</div>}
                {c.is_household && <Badge className="bg-amber-500 text-white text-[9px] py-0 h-4 px-1.5 mt-1">HOUSEHOLD</Badge>}
              </label>
            ))}
          </section>
        </div>

        <div className="border-t px-5 py-4 space-y-3 bg-slate-50">
          <div>
            <Label className="text-xs font-semibold">Resolution</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {RESOLUTIONS.filter((r) => !r.hidden).map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setResolution(r.id)}
                  className={`text-xs px-3 py-1.5 rounded-md border ${resolution === r.id ? (r.danger ? 'bg-rose-600 border-rose-600 text-white' : 'bg-emerald-600 border-emerald-600 text-white') : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'}`}
                  data-testid={`ai-res-${r.id}`}
                >
                  {r.label}{r.danger ? ' *' : ''}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-slate-500 mt-1">* Requires explicit confirmation.</p>
          </div>
          {chosen?.requiresClient && (
            <div>
              <Label className="text-xs">Target client / deal ID</Label>
              <Input value={targetClientId} onChange={(e) => setTargetClientId(e.target.value)} className="h-8" data-testid="ai-target-input" />
            </div>
          )}
          <div>
            <Label className="text-xs">Operator note</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional context for the audit trail." data-testid="ai-note" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={busy} data-testid="ai-cancel">Cancel</Button>
            <Button onClick={submit} disabled={busy} data-testid="ai-apply">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Apply {chosen?.label}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex gap-2">
      <dt className="text-[10px] uppercase tracking-wider text-slate-400 w-32 shrink-0 pt-0.5">{label}</dt>
      <dd className="text-sm text-slate-800 flex-1 truncate">{value || <span className="text-slate-300">—</span>}</dd>
    </div>
  );
}
